// What can be migrated into the pawn module, and the exact sheet each dataset
// expects. One spec per dataset; ./importSheet turns any of them into a
// downloadable template and reads a filled one back.
//
// Read this file as the migration contract. A column's `whatToFill` is what the
// shop's own staff will read in the template, so it is written for them, not for
// a developer — and `goesTo` names the table/column it lands in so the importer
// that gets wired up later has nowhere to guess.
//
// STATUS —
//   "live"  : the import runs end to end today.
//   "draft" : the template and the sheet check are live, so a shop can prepare
//             and validate its data now; the create step is still being built.
//             Keep the columns of a draft spec in step with the API as it lands.
//
// No React/MUI here: icons and colours belong to the screen (PawnImportData.js),
// so this stays a plain, testable description of the data.
import {
  COLUMNS as CUSTOMER_COLUMNS,
  TEMPLATE_FILE_NAME as CUSTOMER_TEMPLATE_FILE,
  downloadCustomerTemplate,
} from "../pawnCustomer/bulkCustomerSheet";
import { withLetters } from "./importSheet";

// Column scopes, for a sheet where several rows make one record (a loan and its
// ornaments). "record" columns are filled on the first row of the loan only;
// "item" columns are filled on every row.
const RECORD = "record";
const ITEM = "item";

// ─── 1 · Customers & nominees ─────────────────────────────────────────────────
// Already built, and reused as-is: the Pawn Customer Master's bulk create is the
// same import, so this entry hands off to that dialog rather than growing a
// second, half-as-good copy of it.
const customers = {
  key: "customers",
  step: 1,
  label: "Customers",
  title: "Customers & Nominees",
  blurb:
    "Every person you lend to, and the nominees attached to them. Import this first — a loan can only be attached to a customer who already exists.",
  rowMeaning:
    "one person — a customer, or a nominee linked to one by Parent Mobile",
  status: "live",
  handler: "bulkCustomer", // opens BulkCustomerDialog instead of the generic one
  fileName: CUSTOMER_TEMPLATE_FILE,
  downloadTemplate: downloadCustomerTemplate,
  columns: CUSTOMER_COLUMNS,
};

// ─── 2 · Accounts (Account Master) ────────────────────────────────────────────
// The shop's chart of accounts. Two columns, and that is the whole sheet by the
// client's instruction: the account's NAME, and the KEY it had in the old app.
//
// The old key is the point of this dataset. The old software refers to accounts
// by that key everywhere — it is what sits in the old ledger's account column —
// so storing it as account_master.old_id is what lets an imported transaction
// find the account it was posted against. It is also the duplicate guard: an
// account whose key is already here is skipped, so this sheet can be re-run.
//
// Every account arrives under the INCOME category and outside any group. That
// is deliberate and temporary — see the note in the spec below.
const accountColumns = withLetters([
  {
    header: "Account Name",
    key: "accountName",
    mandatory: true,
    type: "text",
    preview: true,
    whatToFill:
      "The account's name, exactly as it reads in your old software's account list — 'CASH IN HAND', 'HDFC CURRENT 4412', 'INTEREST RECEIVED'. This is the name you will pick from when you set your pawn accounts up afterwards, so keep it recognisable.",
    example: "CASH IN HAND",
    goesTo: "account_master.account_name",
  },
  {
    header: "Old Account Key",
    key: "oldAccountKey",
    mandatory: true,
    type: "text",
    preview: true,
    whatToFill:
      "The account's own code or key in your old software — the 'akey' column, usually a short number like 513. Copy it exactly. This is what links your old loans and receipts to the right account, and what stops an account being imported twice if you run this sheet again.",
    example: "513",
    goesTo: "account_master.old_id",
  },
]);

const accounts = {
  key: "accounts",
  step: 2,
  label: "Accounts",
  title: "Accounts (Account Master)",
  blurb:
    "Your chart of accounts — the cash, bank and interest heads your old books posted to. Bring these across before your loans, so a loan paid out through the bank can be attached to the right account.",
  rowMeaning: "one account",
  status: "live",
  // A master, not a transaction: it posts nothing to the ledger, belongs to no
  // financial year and touches no voucher counter. The dialog reads this to
  // skip the store-readiness check — which would otherwise refuse a store
  // whose Pawn Settings have no accounts named yet, the very deadlock this
  // sheet exists to break.
  kind: "master",
  fileName: "Pawn_Import_Accounts_Template.xlsx",
  columns: accountColumns,
  notes: [
    "Fill your rows into the 'Ready To Fill' sheet. It already has the correct header row; the 'Sample Data' sheet is a worked example and is never imported.",
    "BOTH columns are required on every row. The name is what you will see in the app; the old key is what your old transactions point at.",
    "Copy the old key exactly as your old software shows it. It is treated as text, so 513 and 0513 are two different accounts — which is correct, because that is how your old books read them.",
    "An account whose old key is already in the app is SKIPPED and left completely untouched — not renamed, not merged. So it is safe to re-run this sheet after fixing a few rows, and safe to run it again next week with more accounts added to the bottom.",
    "Because of that, this sheet never edits an account. If you need to correct a name that is already imported, change it on the Accounts screen — re-uploading will not do it.",
    "Two accounts may share a name (a 'CASH' at each branch, say). Both are kept and the report says so — their old keys tell them apart.",
    "EVERY account arrives under the INCOME category, in no group. That is on purpose for now, and it means the Trial Balance and Profit & Loss will read wrongly until the categories are set on the Accounts screen. Nothing else is affected — your loans, receipts and ledger postings are unaffected by an account's category.",
    "Opening balances are NOT imported. Enter them on the Accounts screen, or bring them in as an opening journal, once the accounts are here.",
    "Groups are not imported either — every account lands at the top level. Move them into groups on the Accounts screen afterwards if you want the tree back.",
    "These accounts are shared with the Accounts module, so they will appear on the /accounts screens too. That is the same chart of accounts, not a pawn-only copy.",
    "After this sheet: open Pawn Settings and set your pledge, cash and interest accounts from the list you have just imported. Loans and receipts post to THOSE, not to whichever account an old voucher happened to name.",
  ],
  sampleRows: [
    { accountName: "CASH IN HAND", oldAccountKey: "513" },
    { accountName: "HDFC CURRENT 4412", oldAccountKey: "514" },
    { accountName: "INTEREST RECEIVED", oldAccountKey: "93" },
    { accountName: "PLEDGE LOANS", oldAccountKey: "201" },
  ],
};

// ─── 3 · Loans (Payment screen) ───────────────────────────────────────────────
const loanColumns = withLetters([
  {
    header: "Loan Voucher No",
    key: "voucherNo",
    scope: RECORD,
    mandatory: true,
    type: "text",
    preview: true,
    whatToFill:
      "The loan / ticket number from your old software. Rows that share one number are ONE loan with several ornaments — repeat the number, or leave it blank on the extra ornament rows.",
    example: "1042",
    goesTo: "pawn_broking.voucher_no",
  },
  {
    header: "Ledger Voucher No",
    key: "ledgerVoucherNo",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill:
      "The voucher number your old software gave this loan IN THE LEDGER — usually the reference column beside the loan, like PAY3. Fill it and your new books read back with the numbers you already know. Leave it blank and the app gives the loan its own new number instead.",
    example: "PAY3",
    goesTo:
      "pawn_broking.voucher_ref, and the vouchernumber on every ledger row this loan posts",
  },
  {
    header: "Loan Date",
    key: "loanDate",
    scope: RECORD,
    mandatory: true,
    type: "date",
    preview: true,
    whatToFill:
      "The date the loan was given, DD/MM/YYYY. Interest is charged from this date, and the metal rate that applies is the one nearest before it.",
    example: "14/03/2025",
    goesTo: "pawn_broking.transaction_date",
  },
  {
    header: "Customer Mobile",
    key: "customerMobile",
    scope: RECORD,
    mandatory: false,
    type: "mobile",
    preview: true,
    whatToFill:
      "10-digit mobile of the customer this loan belongs to. They must already be in the app — import Customers first. Fill Customer Aadhar or Party Code as well if the number is shared by more than one person.",
    example: "9876543210",
    goesTo: "pawn_broking.cust_id (resolved from the mobile number)",
  },
  {
    header: "Customer Aadhar",
    key: "customerAadhar",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill:
      "Aadhar number of the customer. Use it when one mobile number belongs to several people — an Aadhar pins down exactly one.",
    example: "234567890123",
    goesTo: "pawn_broking.cust_id (resolved from the Aadhar document)",
  },
  {
    header: "Customer Party Code",
    key: "customerPartyCode",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill:
      "Your own customer code, if you imported one with the customer. A third way to name the customer, and the surest if your old software used codes.",
    example: "PC-1042",
    goesTo: "pawn_broking.cust_id (resolved from customer_master.party_code)",
  },
  {
    header: "Scheme",
    key: "scheme",
    scope: RECORD,
    mandatory: false,
    type: "text",
    preview: true,
    whatToFill:
      "Name of the scheme this loan runs on, spelt as it is in Scheme Master. A scheme the app does not know is not a problem — the loan still imports and simply uses the Interest % on this row.",
    example: "GOLD 12M",
    goesTo: "pawn_broking.scheme_id (resolved by scheme name)",
  },
  {
    header: "Interest %",
    key: "interestRate",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill:
      "Monthly interest rate actually charged on this loan. Leave blank to take the scheme's own rate.",
    example: "1.5",
    goesTo: "pawn_broking.loan_interest",
  },
  {
    header: "Loan Amount",
    key: "loanAmount",
    scope: RECORD,
    mandatory: true,
    type: "number",
    preview: true,
    whatToFill:
      "Principal handed to the customer, in rupees. Not the total of the ornament values — what was actually lent.",
    example: "50000",
    goesTo: "pawn_broking.loan_amount",
  },
  {
    header: "Insurance %",
    key: "insuranceRate",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill:
      "Insurance percentage charged on this loan. Leave blank to take the scheme's own rate.",
    example: "0.1",
    goesTo: "pawn_broking.insurance_percentage",
  },
  {
    header: "One Month Deduct",
    key: "oneMonthDeduct",
    scope: RECORD,
    mandatory: false,
    type: "yesno",
    whatToFill:
      "Yes if one month's interest was deducted from the loan amount at the time of pledge. Blank counts as No.",
    example: "No",
    goesTo: "pawn_broking.one_month_deduct",
  },
  {
    header: "Other Charges",
    key: "otherCharges",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill:
      "Any charge collected at the time of the loan — appraisal, packing, documentation. Blank means none.",
    example: "100",
    goesTo: "pawn_broking.other_charges",
  },
  {
    header: "First Month Interest",
    key: "firstMonthInterest",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill:
      "The first month's interest in rupees, if your old software stored the figure. Fill it to have your exact number used; leave it blank and the app works it out as loan amount × interest %.",
    example: "750",
    goesTo: "pawn_broking.interest_amount",
  },
  {
    header: "Settled",
    key: "settled",
    scope: RECORD,
    mandatory: false,
    type: "yesno",
    whatToFill:
      "Yes if this loan was already redeemed and closed in your old software. It then comes across closed, and you only need step 4 if you also want its closing receipt. Blank counts as No.",
    example: "No",
    goesTo: "pawn_broking.is_settled",
  },
  {
    header: "Settled Date",
    key: "settledDate",
    scope: RECORD,
    mandatory: false,
    type: "date",
    whatToFill: "The date it was closed. Only read when Settled is Yes.",
    example: "13/04/2026",
    goesTo: "pawn_broking.settled_date",
  },
  {
    header: "Interest Paid Upto",
    key: "interestUptoDate",
    scope: RECORD,
    mandatory: false,
    type: "date",
    whatToFill:
      "ONLY IF YOU ARE NOT IMPORTING THIS LOAN'S RECEIPTS: the date interest is already paid up to. Get it right — every rupee the app charges from now on starts here. If you ARE importing the receipts (step 3), leave this blank; the receipts say it better than one date can.",
    example: "14/05/2025",
    goesTo:
      "pawn_broking.int_upto_date — read as the loan's position until its first real receipt exists",
  },
  {
    header: "Principal Already Repaid",
    key: "principalRepaid",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill:
      "ONLY IF YOU ARE NOT IMPORTING THIS LOAN'S RECEIPTS: how much of the principal the customer has already paid back, so the app knows what is still outstanding. Leave blank if nothing has been repaid, or if you are importing the receipts in step 3.",
    example: "5000",
    goesTo:
      "pawn_broking.principal_received — the loan's opening balance until its first real receipt exists",
  },
  {
    header: "Collect By Date",
    key: "collectByDate",
    scope: RECORD,
    mandatory: false,
    type: "date",
    whatToFill:
      "The date the loan is due to be collected / redeemed by, if your old software tracked one.",
    example: "14/03/2026",
    goesTo: "pawn_broking.collect_by_date",
  },
  {
    header: "Floor",
    key: "floor",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill:
      "Where the packet physically sits — the floor / shelf name from Floor Master. It must already exist there.",
    example: "GF-A-12",
    goesTo: "pawn_broking.floor_id (resolved by floor name)",
  },
  {
    header: "Tare Weight (g)",
    key: "tareWeight",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill:
      "Weight of the packing itself — thread, tags, box — in grams. Blank means none.",
    example: "1.2",
    goesTo: "pawn_broking.tare_wt",
  },
  {
    header: "Nominee Name",
    key: "nomineeName",
    scope: RECORD,
    mandatory: false,
    type: "text",
    upper: true,
    whatToFill:
      "Name of the nominee recorded on this loan. It should be one of that customer's nominees; if it is not, the loan is still imported with the name on it.",
    example: "ANAND KUMAR",
    goesTo: "pawn_broking.nominee_name / nominee_id",
  },
  {
    header: "Pay Mode",
    key: "payMode",
    scope: RECORD,
    mandatory: false,
    type: "list",
    options: ["Cash", "Bank", "Both"],
    whatToFill: "How the loan amount was handed over. Blank counts as Cash.",
    example: "Cash",
    goesTo: "pawn_broking.pay_mode",
  },
  {
    header: "Cash Amount",
    key: "cashAmount",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill:
      "Part of the loan paid in cash. Leave blank on a pure-cash loan — the whole loan amount is taken as cash.",
    example: "50000",
    goesTo: "pawn_broking.cash_amount_received",
  },
  {
    header: "Bank Account",
    key: "bankAccount",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill:
      "Name of the bank / UPI account the money went out from, exactly as it appears in Account Master. Only for Bank or Both.",
    example: "HDFC CURRENT 4412",
    goesTo: "ledger posting — the bank leg of the loan voucher",
  },
  {
    header: "Bank Amount",
    key: "bankAmount",
    scope: RECORD,
    mandatory: false,
    type: "number",
    whatToFill: "Part of the loan transferred to the customer's bank / UPI.",
    example: "0",
    goesTo: "pawn_broking.payment_amount (bank leg)",
  },
  {
    header: "Transaction Reference",
    key: "transactionRef",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill:
      "UTR / UPI / cheque reference of the bank transfer, if there was one.",
    example: "UPI-4412998877",
    goesTo: "pawn_broking.transaction_ref",
  },
  {
    header: "Remarks",
    key: "remarks",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill: "Anything you want kept against this loan. Free text.",
    example: "Migrated from old software",
    goesTo: "pawn_broking.remarks",
  },
  {
    header: "Item Name",
    key: "itemName",
    scope: ITEM,
    mandatory: true,
    type: "text",
    upper: true,
    preview: true,
    whatToFill:
      "The ornament pledged — Bangle, Chain, Ring… One ornament per row; a loan with four ornaments is four rows sharing one Loan Voucher No.",
    example: "BANGLE",
    goesTo: "ornaments_details.item_name",
  },
  {
    header: "Metal Type",
    key: "metalType",
    scope: ITEM,
    mandatory: false,
    type: "text",
    upper: true,
    whatToFill:
      "GOLD, SILVER… as named in Metal Type Master. A name it does not recognise still imports — the ornament is filed under the store's main metal and the report tells you which ones.",
    example: "GOLD",
    goesTo: "ornaments_details.metal_type_id (resolved by metal name)",
  },
  {
    header: "Purity %",
    key: "purityPercentage",
    scope: ITEM,
    mandatory: false,
    type: "number",
    whatToFill:
      "Purity of this ornament as a percentage — 91.6 for 22ct, 75 for 18ct. Leave blank to take the metal's default purity.",
    example: "91.6",
    goesTo: "ornaments_details.purity_percentage",
  },
  {
    header: "Rate (Rs/g)",
    key: "metalRate",
    scope: ITEM,
    mandatory: false,
    type: "number",
    whatToFill:
      "Rate per gram used when this loan was made. Leave blank to take the rate that applied on the loan date.",
    example: "6250",
    goesTo: "ornaments_details.metal_type_rate",
  },
  {
    header: "Pcs",
    key: "itemPcs",
    scope: ITEM,
    mandatory: false,
    type: "number",
    whatToFill: "Number of pieces of this ornament. Blank counts as 1.",
    example: "2",
    goesTo: "ornaments_details.item_pcs",
  },
  {
    header: "Gross (g)",
    key: "itemGross",
    scope: ITEM,
    mandatory: true,
    type: "number",
    preview: true,
    whatToFill: "Gross weight of this ornament in grams, stones included.",
    example: "16.400",
    goesTo: "ornaments_details.item_gross",
  },
  {
    header: "Net (g)",
    key: "itemNet",
    scope: ITEM,
    mandatory: true,
    type: "number",
    preview: true,
    whatToFill:
      "Net metal weight in grams, stones and wax deducted. Never more than the gross.",
    example: "15.900",
    goesTo: "ornaments_details.item_net",
  },
  {
    header: "Item Value",
    key: "itemValue",
    scope: ITEM,
    mandatory: true,
    type: "number",
    whatToFill:
      "What this ornament was valued at, in rupees. The loan amount is normally a percentage of the total of these.",
    example: "99375",
    goesTo: "ornaments_details.item_value",
  },
  // The two old-app keys, appended LAST and to stay last: a sheet whose header
  // row is unreadable is read strictly by POSITION, so inserting a column
  // higher up would shift every column after it for every template a shop has
  // already downloaded and filled in.
  {
    header: "Old Customer ID",
    key: "customerOldId",
    scope: RECORD,
    mandatory: false,
    type: "text",
    preview: true,
    whatToFill:
      "The customer's ID in your OLD software — the number your old loan record itself points at. THE SUREST way to name the customer: fill it and there is nothing to guess. It only works if you imported that customer with their Old Customer ID filled in on the Customers sheet. Leave blank to fall back to mobile / Aadhar / party code.",
    example: "412",
    goesTo:
      "pawn_broking.cust_id (resolved through customer_master.old_id — takes priority over the mobile, Aadhar and party code columns)",
  },
  {
    header: "Bank Account Old Key",
    key: "bankAccountOldId",
    scope: RECORD,
    mandatory: false,
    type: "text",
    whatToFill:
      "The 'akey' of the bank / UPI account in your old software, if the loan was not paid fully in cash. Surer than the name, because two accounts are allowed to share a name. Needs the Accounts sheet imported first. Leave blank to go by the Bank Account name instead.",
    example: "514",
    goesTo:
      "pawn_broking.bank_account_id_1 (resolved through account_master.old_id — takes priority over the Bank Account name)",
  },
]);

const loans = {
  key: "loans",
  step: 3,
  label: "Loans",
  title: "Loans (Payment Screen)",
  blurb:
    "Every pledge still on your books, with its ornaments. This is the big one — the loan, who it belongs to, what secures it and what is already paid.",
  rowMeaning:
    "one ornament. Rows sharing a Loan Voucher No make one loan, so a loan with four ornaments is four rows",
  status: "live",
  fileName: "Pawn_Import_Loans_Template.xlsx",
  groupBy: "voucherNo",
  groupNoun: "loan",
  dependsOn: ["customers"],
  columns: loanColumns,
  notes: [
    "Fill your rows into the 'Ready To Fill' sheet. It already has the correct header row; the 'Sample Data' sheet is a worked example and is never imported.",
    "Columns are matched by header name when the header row is intact; if the headers are unrecognisable they are read strictly by position. So rename OR reorder — never both.",
    "ONE LOAN, SEVERAL ORNAMENTS: put each ornament on its own row and repeat the Loan Voucher No (or leave it blank on the extra rows). Keep a loan's rows together, one after another.",
    "On those extra ornament rows every loan-level column may be left blank — they are taken from the first row of the loan. Only the ornament columns are needed.",
    "MANDATORY on the first row of a loan: Loan Voucher No, Loan Date, Loan Amount, and enough to name the customer (mobile, Aadhar or party code). On every ornament row: Item Name, Gross and Net.",
    "The customer must already exist. Import Customers first, then come back here — a loan is never allowed to create a customer, because a half-typed name would then be attached to real money.",
    "Set your schemes, metal types and floors up first and spell them the same way here — but a name the app does not recognise never costs you the loan. It imports without that link and the report lists every one, so you can set them afterwards.",
    "Interest Paid Upto is the single most important date on the sheet: from the day you import, the app charges interest from there. Leave it blank only when the customer has paid nothing yet.",
    "EVERY loan goes here, open or closed. A loan that was already redeemed still needs its pledge row before its receipts and its closing can be attached in steps 3 and 4 — nothing later can attach to a loan that was never brought across. Mark it Settled and it arrives closed.",
    "OLD CUSTOMER ID is the surest way to name the customer, and the reason the Customers sheet has that column too. Your old software records a loan against a customer NUMBER — fill that number here and there is nothing to guess. It beats every other column: if it is filled, the mobile, Aadhar and party code are not even looked at.",
    "An Old Customer ID that is filled in but matches nobody STOPS the row, on purpose. You have told us exactly which customer this is, so falling back to a shared mobile number could attach the loan to the wrong person. Import that customer first, or clear the cell.",
    "Bank Account Old Key does the same job for the money leg — two accounts are allowed to share a name, so the old key is surer. Unlike the customer, an unrecognised account never costs you the loan: the payout is booked as cash and the report says so.",
    "ONE thing can stop a loan being imported: not knowing whose it is. Everything else — an unknown scheme, floor, metal type or bank account — imports anyway and is listed in the report for you to set afterwards.",
    "Interest Paid Upto and Principal Already Repaid are for shops NOT importing receipts: they tell the app where a half-served loan stands. Importing the receipts says the same thing more precisely, and takes over the moment the first one lands.",
    "The Ledger Voucher No column is what keeps your books reading the way they always have. Fill it from your old ledger's voucher column and every posting this loan makes carries that number.",
    "Amounts are plain numbers in rupees — commas and a Rs sign are ignored. Weights are in grams, up to three decimals.",
    "Dates accept DD/MM/YYYY, YYYY-MM-DD or a real Excel date cell. A date that cannot be read blocks the row rather than saving a wrong one.",
    "Every loan is created under the store you are logged into, and in the book (official / unofficial) you are currently in. Check the mode before you import.",
    "Photos, fingerprints and scanned documents cannot come from a spreadsheet. Import the numbers, then open a loan on the Payment screen to attach anything else.",
  ],
  sampleRows: [
    {
      voucherNo: "1042",
      ledgerVoucherNo: "PAY3",
      loanDate: "14/03/2025",
      customerOldId: "412",
      customerMobile: "9876543210",
      customerAadhar: "234567890123",
      customerPartyCode: "PC-1042",
      scheme: "GOLD 12M",
      interestRate: "1.5",
      loanAmount: "50000",
      insuranceRate: "0.1",
      oneMonthDeduct: "No",
      otherCharges: "100",
      interestUptoDate: "14/05/2025",
      collectByDate: "14/03/2026",
      floor: "GF-A-12",
      tareWeight: "1.2",
      nomineeName: "ANAND KUMAR",
      payMode: "Cash",
      cashAmount: "50000",
      bankAccount: "",
      bankAmount: "",
      transactionRef: "",
      remarks: "Migrated from old software",
      itemName: "BANGLE",
      metalType: "GOLD",
      purityPercentage: "91.6",
      metalRate: "6250",
      itemPcs: "2",
      itemGross: "16.400",
      itemNet: "15.900",
      itemValue: "99375",
    },
    {
      // Same loan, second ornament: voucher repeated, every loan-level column
      // left blank on purpose — this is the shape a continuation row takes.
      voucherNo: "1042",
      itemName: "CHAIN",
      metalType: "GOLD",
      purityPercentage: "91.6",
      metalRate: "6250",
      itemPcs: "1",
      itemGross: "8.250",
      itemNet: "8.100",
      itemValue: "50625",
    },
    {
      voucherNo: "1043",
      ledgerVoucherNo: "PAY4",
      loanDate: "02/04/2025",
      customerOldId: "508",
      customerMobile: "9845012399",
      scheme: "SILVER 6M",
      loanAmount: "12000",
      oneMonthDeduct: "Yes",
      interestUptoDate: "02/05/2025",
      floor: "GF-B-04",
      payMode: "Both",
      cashAmount: "2000",
      bankAccount: "HDFC CURRENT 4412",
      bankAmount: "10000",
      bankAccountOldId: "514",
      transactionRef: "UPI-4412998877",
      itemName: "ANKLET",
      metalType: "SILVER",
      purityPercentage: "92.5",
      metalRate: "78",
      itemPcs: "2",
      itemGross: "210.000",
      itemNet: "205.500",
      itemValue: "16029",
    },
  ],
};

// ─── 4 · Receipts (Receipt screen) ────────────────────────────────────────────
const receiptColumns = withLetters([
  {
    header: "Loan Voucher No",
    key: "loanVoucherNo",
    mandatory: true,
    type: "text",
    preview: true,
    whatToFill:
      "The loan this money was paid against — the same voucher number you used in the Loans sheet. The loan must already be imported.",
    example: "1042",
    goesTo: "pawn_receipts.pawn_id (resolved from the loan's voucher_no)",
  },
  {
    header: "Receipt No",
    key: "receiptNo",
    mandatory: false,
    type: "text",
    whatToFill:
      "Your old software's number for this receipt — the one in your ledger and on the customer's card. Fill it and your books read back with the numbers you already know. Leave it blank to have the app number the receipt itself.",
    example: "REC32",
    goesTo:
      "pawn_receipts.voucher_ref, and the vouchernumber on every ledger row this receipt posts",
  },
  {
    header: "Receipt Date",
    key: "receiptDate",
    mandatory: true,
    type: "date",
    preview: true,
    whatToFill:
      "The date the customer paid, DD/MM/YYYY. Receipts on one loan are applied in date order, so this is what decides the running balance.",
    example: "14/05/2025",
    goesTo: "pawn_receipts.receipt_date",
  },
  {
    header: "Amount Paid",
    key: "amount",
    mandatory: true,
    type: "number",
    preview: true,
    whatToFill:
      "Total rupees taken from the customer on this receipt — interest, insurance and principal together.",
    example: "1250",
    goesTo: "pawn_receipts.amount",
  },
  {
    header: "Interest Paid",
    key: "interestDeducted",
    mandatory: false,
    type: "number",
    preview: true,
    whatToFill:
      "How much of the amount went towards interest. Whatever is left after interest, insurance and charges comes off the principal — so these columns are how the old receipt is reproduced exactly rather than recalculated.",
    example: "1125",
    goesTo: "pawn_receipts.interest_deducted",
  },
  {
    header: "Interest Due",
    key: "interestDue",
    mandatory: false,
    type: "number",
    whatToFill:
      "What interest was DUE at the time — fill it only when it differs from what was paid. That is how a customer who paid short is recorded: the shortfall carries forward and is charged again on the next receipt, exactly as your old software did it.",
    example: "1125",
    goesTo:
      "pawn_receipts.interest_due, and the carry-forward on pawn_receipts.carryover_interest",
  },
  {
    header: "Insurance Paid",
    key: "insuranceDeducted",
    mandatory: false,
    type: "number",
    whatToFill: "How much of the amount went towards insurance.",
    example: "50",
    goesTo: "pawn_receipts.insurance_deducted",
  },
  {
    header: "Interest Discount",
    key: "interestDiscount",
    mandatory: false,
    type: "number",
    whatToFill:
      "Interest waived on this receipt, in rupees. This is money the customer did NOT pay, so it is not part of Amount Paid.",
    example: "0",
    goesTo: "pawn_receipts.interest_discount",
  },
  {
    header: "Other Charges",
    key: "otherCharges",
    mandatory: false,
    type: "number",
    whatToFill: "Any charge collected along with this receipt.",
    example: "0",
    goesTo: "pawn_receipts.other_charges",
  },
  {
    header: "Principal Reduction",
    key: "principalReduction",
    mandatory: false,
    type: "number",
    preview: true,
    whatToFill:
      "How much of the amount came off the principal. Blank means the receipt was interest only.",
    example: "75",
    goesTo: "pawn_receipts.principal_reduction",
  },
  {
    header: "Paid Upto Date",
    key: "paidUpToDate",
    mandatory: false,
    type: "date",
    whatToFill:
      "The date interest stands paid up to after this receipt. Leave blank to have the app work it out from the amount.",
    example: "14/06/2025",
    goesTo: "pawn_receipts.paid_up_to_date",
  },
  {
    header: "Pay Mode",
    key: "payMode",
    mandatory: false,
    type: "list",
    options: ["Cash", "Bank", "Both"],
    whatToFill: "How the customer paid. Blank counts as Cash.",
    example: "Cash",
    goesTo: "pawn_receipts.pay_mode",
  },
  {
    header: "Cash Amount",
    key: "cashAmount",
    mandatory: false,
    type: "number",
    whatToFill:
      "Part paid in cash. Leave blank on a pure-cash receipt — the whole amount is taken as cash.",
    example: "1250",
    goesTo: "pawn_receipts.cash_amount_received",
  },
  {
    header: "Bank Account",
    key: "bankAccount",
    mandatory: false,
    type: "text",
    whatToFill:
      "Name of the bank / UPI account the money came into, exactly as in Account Master.",
    example: "HDFC CURRENT 4412",
    goesTo: "ledger posting — the bank leg of the receipt voucher",
  },
  {
    header: "Bank Amount",
    key: "bankAmount",
    mandatory: false,
    type: "number",
    whatToFill: "Part paid by bank transfer / UPI / card.",
    example: "0",
    goesTo: "pawn_receipts (bank leg)",
  },
  {
    header: "Transaction Reference",
    key: "transactionRef",
    mandatory: false,
    type: "text",
    whatToFill:
      "UTR / UPI / cheque reference of the transfer, if there was one.",
    example: "UPI-77661234",
    goesTo: "pawn_receipts.transaction_ref",
  },
  {
    header: "Remarks",
    key: "remarks",
    mandatory: false,
    type: "text",
    whatToFill: "Anything you want kept against this receipt. Free text.",
    example: "Interest for 2 months",
    goesTo: "pawn_receipts.remarks",
  },
  // Appended last, and to stay last — see the note on the loans sheet. There is
  // no Old Customer ID here: a receipt is attached to its LOAN by voucher
  // number, and the customer comes from the loan.
  {
    header: "Bank Account Old Key",
    key: "bankAccountOldId",
    mandatory: false,
    type: "text",
    whatToFill:
      "The 'akey' of the bank / UPI account in your old software, if this receipt was not taken fully in cash. Surer than the name, because two accounts are allowed to share a name. Needs the Accounts sheet imported first. Leave blank to go by the Bank Account name instead.",
    example: "514",
    goesTo:
      "pawn_receipts.bank_account_id_1 (resolved through account_master.old_id — takes priority over the Bank Account name)",
  },
]);

const receipts = {
  key: "receipts",
  step: 4,
  label: "Receipts",
  title: "Receipts (Receipt Screen)",
  blurb:
    "The interest and part-payments already collected on those loans. Import them and each loan's history — and its balance — reads exactly as it did in your old software.",
  rowMeaning: "one receipt taken against one loan",
  status: "live",
  fileName: "Pawn_Import_Receipts_Template.xlsx",
  dependsOn: ["loans"],
  columns: receiptColumns,
  notes: [
    "Fill your rows into the 'Ready To Fill' sheet. The 'Sample Data' sheet is a worked example and is never imported.",
    "Columns are matched by header name when the header row is intact; otherwise they are read strictly by position. Rename OR reorder — never both.",
    "The loan must already be imported. Loans first, receipts second — a receipt cannot create the loan it belongs to.",
    "MANDATORY on every row: Loan Voucher No, Receipt Date and Amount Paid. Everything else is optional.",
    "SEVERAL RECEIPTS ON ONE LOAN: just put each on its own row with the same Loan Voucher No. They do not have to be next to each other, and they do not have to be in date order — they are applied oldest first whatever order the sheet is in.",
    "If you leave Interest Paid / Insurance Paid / Principal Reduction blank, the app splits the amount itself using the loan's scheme, exactly as the Receipt screen would. Fill them in when your old software split it differently and you want that history kept as it was.",
    "A receipt that closed the loan is NOT imported here — that is a Settlement. Import only the payments made while the loan stayed open.",
    "Do not import a receipt dated before its loan date, and do not let the receipts on a loan add up to more than what was owed — such rows are refused rather than quietly adjusted.",
    "Amounts are plain numbers in rupees; commas and a Rs sign are ignored.",
    "Dates accept DD/MM/YYYY, YYYY-MM-DD or a real Excel date cell.",
    "Receipts are created in the book (official / unofficial) you are currently in, and against the store you are logged into.",
  ],
  sampleRows: [
    {
      loanVoucherNo: "1042",
      receiptNo: "REC32",
      receiptDate: "14/05/2025",
      amount: "1250",
      interestDeducted: "1125",
      interestDue: "1125",
      insuranceDeducted: "50",
      interestDiscount: "0",
      otherCharges: "0",
      principalReduction: "75",
      paidUpToDate: "14/06/2025",
      payMode: "Cash",
      cashAmount: "1250",
      bankAccount: "",
      bankAmount: "",
      transactionRef: "",
      remarks: "Interest for 2 months",
    },
    {
      loanVoucherNo: "1042",
      receiptNo: "REC48",
      receiptDate: "18/07/2025",
      amount: "5000",
      principalReduction: "3800",
      payMode: "Both",
      cashAmount: "1000",
      bankAccount: "HDFC CURRENT 4412",
      bankAmount: "4000",
      bankAccountOldId: "514",
      transactionRef: "UPI-77661234",
      remarks: "Part principal",
    },
    {
      // Paid short: 250 was due, 180 came in. The missing 70 carries forward
      // and is charged again next time — which is only possible because the
      // Interest Due column says what was owed.
      loanVoucherNo: "1043",
      receiptDate: "02/06/2025",
      amount: "180",
      interestDeducted: "180",
      interestDue: "250",
      payMode: "Cash",
      remarks: "Part interest — 70 carried forward",
    },
  ],
};

// ─── 5 · Settlements ──────────────────────────────────────────────────────────
const settlementColumns = withLetters([
  {
    header: "Loan Voucher No",
    key: "loanVoucherNo",
    mandatory: true,
    type: "text",
    preview: true,
    whatToFill:
      "The loan that was closed — the same voucher number you used in the Loans sheet.",
    example: "1039",
    goesTo: "pawn_broking.is_settled / settled_date on that loan",
  },
  {
    header: "Settlement Date",
    key: "settlementDate",
    mandatory: true,
    type: "date",
    preview: true,
    whatToFill:
      "The date the loan was fully paid off and the ornaments went back to the customer.",
    example: "20/06/2025",
    goesTo: "pawn_broking.settled_date",
  },
  {
    header: "Settlement Receipt No",
    key: "settlementReceiptNo",
    mandatory: false,
    type: "text",
    whatToFill:
      "Your old software's number for the closing receipt. Leave it blank to have the app number it — its own settlement vouchers run H1, H2, H3…",
    example: "REC32",
    goesTo:
      "pawn_receipts.voucher_ref, and the vouchernumber on the ledger rows that close the loan",
  },
  {
    header: "Total Received",
    key: "amount",
    mandatory: true,
    type: "number",
    preview: true,
    whatToFill:
      "Total rupees taken to close the loan — principal, interest, insurance and charges together.",
    example: "52400",
    goesTo: "pawn_receipts.amount on the settlement receipt",
  },
  {
    header: "Interest Paid",
    key: "interestDeducted",
    mandatory: false,
    type: "number",
    preview: true,
    whatToFill:
      "How much of the total was interest. Leave the split columns blank to have the app work it out from the loan.",
    example: "2250",
    goesTo: "pawn_receipts.interest_deducted",
  },
  {
    header: "Insurance Paid",
    key: "insuranceDeducted",
    mandatory: false,
    type: "number",
    whatToFill: "How much of the total was insurance.",
    example: "150",
    goesTo: "pawn_receipts.insurance_deducted",
  },
  {
    header: "Interest Discount",
    key: "interestDiscount",
    mandatory: false,
    type: "number",
    whatToFill:
      "Interest waived to close the loan. Money the customer did NOT pay, so it is not part of Total Received.",
    example: "200",
    goesTo: "pawn_receipts.interest_discount",
  },
  {
    header: "Other Charges",
    key: "otherCharges",
    mandatory: false,
    type: "number",
    whatToFill: "Any charge collected while closing the loan.",
    example: "0",
    goesTo: "pawn_receipts.other_charges",
  },
  {
    header: "Principal Received",
    key: "principalReduction",
    mandatory: false,
    type: "number",
    preview: true,
    whatToFill:
      "Principal repaid at settlement — normally whatever was still outstanding.",
    example: "50000",
    goesTo: "pawn_receipts.principal_reduction",
  },
  {
    header: "Pay Mode",
    key: "payMode",
    mandatory: false,
    type: "list",
    options: ["Cash", "Bank", "Both"],
    whatToFill: "How the closing amount was paid. Blank counts as Cash.",
    example: "Cash",
    goesTo: "pawn_receipts.pay_mode",
  },
  {
    header: "Cash Amount",
    key: "cashAmount",
    mandatory: false,
    type: "number",
    whatToFill:
      "Part paid in cash. Leave blank on a pure-cash settlement — the whole total is taken as cash.",
    example: "52400",
    goesTo: "pawn_receipts.cash_amount_received",
  },
  {
    header: "Bank Account",
    key: "bankAccount",
    mandatory: false,
    type: "text",
    whatToFill:
      "Name of the bank / UPI account the money came into, exactly as in Account Master.",
    example: "HDFC CURRENT 4412",
    goesTo: "ledger posting — the bank leg of the settlement voucher",
  },
  {
    header: "Bank Amount",
    key: "bankAmount",
    mandatory: false,
    type: "number",
    whatToFill: "Part paid by bank transfer / UPI / card.",
    example: "0",
    goesTo: "pawn_receipts (bank leg)",
  },
  {
    header: "Transaction Reference",
    key: "transactionRef",
    mandatory: false,
    type: "text",
    whatToFill:
      "UTR / UPI / cheque reference of the transfer, if there was one.",
    example: "",
    goesTo: "pawn_receipts.transaction_ref",
  },
  {
    header: "Released To",
    key: "releasedTo",
    mandatory: false,
    type: "text",
    upper: true,
    whatToFill:
      "Who actually took the ornaments back, if it was not the customer — a nominee, or a family member.",
    example: "ANAND KUMAR",
    goesTo: "pawn_receipts.remarks (recorded with the settlement)",
  },
  {
    header: "Remarks",
    key: "remarks",
    mandatory: false,
    type: "text",
    whatToFill: "Anything you want kept against this settlement. Free text.",
    example: "Closed in old software",
    goesTo: "pawn_receipts.remarks",
  },
  // Appended last, and to stay last — see the note on the loans sheet. As with
  // receipts, the customer comes from the loan this settlement closes.
  {
    header: "Bank Account Old Key",
    key: "bankAccountOldId",
    mandatory: false,
    type: "text",
    whatToFill:
      "The 'akey' of the bank / UPI account in your old software, if the closing payment was not fully in cash. Surer than the name, because two accounts are allowed to share a name. Needs the Accounts sheet imported first. Leave blank to go by the Bank Account name instead.",
    example: "514",
    goesTo:
      "pawn_receipts.bank_account_id_1 (resolved through account_master.old_id — takes priority over the Bank Account name)",
  },
]);

const settlements = {
  key: "settlements",
  step: 5,
  label: "Settlements",
  title: "Settlements (Closed Loans)",
  blurb:
    "Loans that were already redeemed and closed. Import them only if you want the old history searchable — your live balances do not need them.",
  rowMeaning: "one closed loan and the payment that closed it",
  status: "live",
  fileName: "Pawn_Import_Settlements_Template.xlsx",
  dependsOn: ["loans"],
  columns: settlementColumns,
  notes: [
    "Fill your rows into the 'Ready To Fill' sheet. The 'Sample Data' sheet is a worked example and is never imported.",
    "Columns are matched by header name when the header row is intact; otherwise they are read strictly by position. Rename OR reorder — never both.",
    "A settlement CLOSES a loan, so that loan must already be imported through the Loans sheet — including the ones that were closed long ago, if you want them here.",
    "MANDATORY on every row: Loan Voucher No, Settlement Date and Total Received.",
    "One row per loan. A loan can only be settled once; a second row for the same voucher is refused.",
    "Import the loan's earlier receipts (the Receipts sheet) BEFORE its settlement. A settled loan is read-only afterwards, so a receipt that arrives late has nowhere to go.",
    "The settlement date must not be earlier than the loan date or than the loan's last receipt.",
    "OTP verification is not applied to an imported settlement, even when 'OTP for settlement' is switched on in Pawn Settings.",
    "Settling a loan here does NOT print a ticket or release anything physically — it only records that the loan was closed.",
    "This sheet is optional. If you only care about the loans still running, skip it: nothing else depends on it.",
    "Settlements are created in the book (official / unofficial) you are currently in, and against the store you are logged into.",
  ],
  sampleRows: [
    {
      loanVoucherNo: "1039",
      settlementDate: "20/06/2025",
      settlementReceiptNo: "H-118",
      amount: "52400",
      interestDeducted: "2250",
      insuranceDeducted: "150",
      interestDiscount: "200",
      otherCharges: "0",
      principalReduction: "50000",
      payMode: "Cash",
      cashAmount: "52400",
      bankAccount: "",
      bankAmount: "",
      transactionRef: "",
      releasedTo: "ANAND KUMAR",
      remarks: "Closed in old software",
    },
    {
      loanVoucherNo: "1040",
      settlementDate: "05/07/2025",
      amount: "18600",
      payMode: "Both",
      cashAmount: "600",
      bankAccount: "HDFC CURRENT 4412",
      bankAmount: "18000",
      bankAccountOldId: "514",
      transactionRef: "UPI-99120044",
      remarks: "",
    },
  ],
};

// In migration order — the screen renders them in exactly this sequence, and the
// step numbers are the order the data has to go in.
export const IMPORT_SPECS = [customers, accounts, loans, receipts, settlements];

export const specByKey = (key) => IMPORT_SPECS.find((s) => s.key === key);
